import { z } from 'zod';
import { FIELD_LIMITS } from './constants.js';
import type { QuestionType } from './enums.js';
import type { JobQuestionDto, QuestionSnapshot } from './question.js';

/** Canonical stored shape of a single custom-question answer. */
export type AnswerValue = string | number | boolean | string[] | null;

/** What a form control hands us before validation. */
export type RawAnswerValue = unknown;

export type AnswersInput = Record<string, RawAnswerValue>;
export type AnswersOutput = Record<string, AnswerValue>;

function isEmpty(raw: unknown): boolean {
  if (raw === undefined || raw === null) return true;
  if (typeof raw === 'string') return raw.trim() === '';
  if (Array.isArray(raw)) return raw.length === 0;
  return false;
}

function fail(ctx: z.RefinementCtx, message: string): typeof z.NEVER {
  ctx.addIssue({ code: 'custom', message });
  return z.NEVER;
}

const urlSchema = z.url({ protocol: /^https?$/ }).max(FIELD_LIMITS.url);
const dateSchema = z.iso.date();

interface AnswerRules {
  type: QuestionType;
  required: boolean;
  choices: readonly string[];
}

/**
 * Validates one raw answer against its question definition and normalises it to
 * the canonical stored shape. The same function runs in the browser and at the
 * API boundary, so client-side hints can never diverge from what is accepted.
 */
export function coerceAnswer(raw: unknown, rules: AnswerRules, ctx: z.RefinementCtx): AnswerValue {
  if (isEmpty(raw)) {
    if (rules.required) return fail(ctx, 'This question is required');
    return null;
  }

  switch (rules.type) {
    case 'SHORT_TEXT':
    case 'LONG_TEXT': {
      if (typeof raw !== 'string') return fail(ctx, 'Expected text');
      const max = rules.type === 'SHORT_TEXT' ? FIELD_LIMITS.shortTextAnswer : FIELD_LIMITS.longTextAnswer;
      const value = raw.trim();
      if (value.length > max) return fail(ctx, `Must be ${max} characters or fewer`);
      return value;
    }
    case 'BOOLEAN': {
      if (typeof raw === 'boolean') return raw;
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return fail(ctx, 'Select yes or no');
    }
    case 'NUMBER': {
      const value = typeof raw === 'number' ? raw : Number(String(raw).trim());
      if (!Number.isFinite(value)) return fail(ctx, 'Enter a number');
      return value;
    }
    case 'URL': {
      if (typeof raw !== 'string') return fail(ctx, 'Expected a URL');
      const parsed = urlSchema.safeParse(raw.trim());
      if (!parsed.success) return fail(ctx, 'Enter a valid http(s) URL');
      return parsed.data;
    }
    case 'DATE': {
      if (typeof raw !== 'string') return fail(ctx, 'Expected a date');
      const parsed = dateSchema.safeParse(raw.trim());
      if (!parsed.success) return fail(ctx, 'Enter a valid date');
      return parsed.data;
    }
    case 'SINGLE_SELECT': {
      if (typeof raw !== 'string') return fail(ctx, 'Select one option');
      const value = raw.trim();
      if (!rules.choices.includes(value)) return fail(ctx, 'Select one of the listed options');
      return value;
    }
    case 'MULTI_SELECT': {
      if (!Array.isArray(raw)) return fail(ctx, 'Select one or more options');
      const values = raw.map((item) => String(item).trim()).filter((item) => item !== '');
      if (values.length === 0) {
        if (rules.required) return fail(ctx, 'This question is required');
        return null;
      }
      if (new Set(values).size !== values.length) return fail(ctx, 'Options must not repeat');
      if (values.some((value) => !rules.choices.includes(value))) {
        return fail(ctx, 'Select only from the listed options');
      }
      return values;
    }
    default: {
      const exhaustive: never = rules.type;
      return fail(ctx, `Unsupported question type: ${String(exhaustive)}`);
    }
  }
}

/**
 * Builds an object schema keyed by question id. Unknown keys are stripped, so a
 * stale form for a since-edited job cannot inject answers to removed questions.
 */
export function buildAnswersSchema(questions: readonly JobQuestionDto[]) {
  const shape: Record<string, z.ZodType<AnswerValue, unknown | undefined>> = {};
  for (const question of questions) {
    if (!question.active) continue;
    // `.optional()` keeps the transform running for absent keys, so an omitted
    // optional answer normalises to null instead of failing the object check.
    shape[question.id] = z
      .unknown()
      .optional()
      .transform((raw, ctx) =>
        coerceAnswer(
          raw,
          { type: question.type, required: question.required, choices: question.choices },
          ctx,
        ),
      );
  }
  return z.object(shape);
}

/** Human-readable rendering of a stored answer, used in the admin UI and CSV export. */
export function formatAnswer(value: AnswerValue, snapshot: Pick<QuestionSnapshot, 'type'>): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join('; ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (snapshot.type === 'NUMBER' && typeof value === 'number') return String(value);
  return String(value);
}
